import { jsxs, jsx } from 'react/jsx-runtime';
import { Component } from 'react';
import ndsTokens from 'nicollet-tokens';
import { cap } from '@nicollet/common';
import { IconNavigationCloseCircle } from 'nicollet-icons-react';
import { PopoverTail } from './PopoverTail.js';
import { BORDER_RADIUS, BORDER_WIDTH, SpeechBubbleContent, DismissibleDiv, DismissButton } from './styles.js';

const offsetSideFromOriginAndTailSide = {
  start: {
    top: 'left',
    bottom: 'left',
    left: 'top',
    right: 'top'
  },
  end: {
    top: 'right',
    bottom: 'right',
    left: 'bottom',
    right: 'bottom'
  }
};
const PIXEL_WIDTH_FROM_TAIL_SIZE = {
  sm: 22,
  md: 30,
  lg: 35
};
const variants = {
  dark: {
    backgroundColor: ndsTokens.colors.palette.gray.darkest,
    borderColor: ndsTokens.colors.palette.gray.darkest,
    color: ndsTokens.colors.palette.white
  },
  light: {
    backgroundColor: ndsTokens.colors.palette.gray.light,
    borderColor: ndsTokens.colors.palette.gray.light,
    color: ndsTokens.colors.palette.gray.darkest
  },
  white: {
    backgroundColor: ndsTokens.colors.palette.white,
    borderColor: ndsTokens.colors.palette.white,
    color: ndsTokens.colors.palette.gray.darkest,
    shadow: '0 2px 4px rgba(0, 0, 0, 0.125)'
  },
  outlined: {
    backgroundColor: ndsTokens.colors.palette.white,
    borderColor: ndsTokens.colors.palette.gray.darkest,
    color: ndsTokens.colors.palette.gray.darkest
  },
  warning: {
    backgroundColor: ndsTokens.colors.palette.orange.light,
    borderColor: ndsTokens.colors.border.warning,
    color: ndsTokens.colors.palette.gray.darkest
  }
};
class PopoverInternal extends Component {
  constructor(props) {
    super(props);
    this.isVertical = this.props.tailSide === 'top' || this.props.tailSide === 'bottom';
    this.divRef = null;

    this.setDivRef = el => {
      this.divRef = el;
      if (this.props.bubbleRef) this.props.bubbleRef(el);
    };

    this.updateContentDimensions = () => {
      if (!this.divRef || !this.dimensionsHaveChanged()) return;
      this.setState(() => {
        if (this.divRef) {
          const {
            clientWidth,
            clientHeight
          } = this.divRef;
          const sideLength = this.isVertical ? clientWidth : clientHeight;
          const maxSize = sideLength - (BORDER_RADIUS + BORDER_WIDTH) * 2;
          return {
            contentHeight: clientHeight,
            contentWidth: clientWidth,
            renderedTailWidth: cap((PIXEL_WIDTH_FROM_TAIL_SIZE[this.props.tailSize] || PIXEL_WIDTH_FROM_TAIL_SIZE.md) + 2, maxSize)
          };
        }

        return null;
      });
    }; // since the tail overlaps the bubble by 1 pixel, the true width needs to be 2px larger than the visible width


    this.state = {
      contentHeight: -1,
      contentWidth: -1,
      renderedTailWidth: (PIXEL_WIDTH_FROM_TAIL_SIZE[this.props.tailSize] || PIXEL_WIDTH_FROM_TAIL_SIZE.md) + 2
    };
  }

  componentDidMount() {
    // in order to calculate the tail position correctly, the speech bubble has to know its dimensions
    // window resize does not trigger React to re-render, so it has to be handled explicitly
    window.addEventListener('resize', this.updateContentDimensions); // when the component first loads, store its dimensions

    this.updateContentDimensions();
  }

  componentDidUpdate() {
    // whenever the component is changed, store its new dimensions (if necessary)
    this.updateContentDimensions();
  }

  componentWillUnmount() {
    // clean up the event listener when the component leaves the page
    window.removeEventListener('resize', this.updateContentDimensions);
  }

  calculateOffset() {
    const halfTailWidth = this.state.renderedTailWidth / 2;
    const halfTailHeight = this.state.renderedTailWidth / 4;
    const centerPosition = `calc(50% - ${halfTailWidth - (this.isVertical ? 0 : halfTailHeight)}px)`;

    if (!this.alignmentHasValidOrigin()) {
      return centerPosition; // if no origin is given, default to the center of the side
    }

    const offsetInt = this.props.tailAlignment.offset;

    if (this.state.contentHeight < 0 || this.state.contentWidth < 0 || Number.isNaN(offsetInt)) {
      return centerPosition; // if the offset can't be calculated, default to the center
    }
    /*
     * borderWidth                                                                              borderWidth
     *  |                                                                                            |
     * |-|-------------------------------------- contentSize ---------------------------------------|-|
     */


    const contentSize = this.isVertical ? this.state.contentWidth : this.state.contentHeight; // |---------------------------------------- total border ----------------------------------------| add the border back

    const totalBorder = contentSize + BORDER_WIDTH * 2;
    const minDistanceFromEdge = BORDER_RADIUS + halfTailWidth + BORDER_WIDTH;
    const max = totalBorder - minDistanceFromEdge;
    /*
     * borderWidth        tailWidth/2                                    tailWidth/2            borderWidth
     *  |                      |                                              |                      |
     * |-|-- borderRadius --|-----|------------- valid border -------------|-----|-- borderRadius --|-|
     * |                          |                                        |                          |
     * 0                         min                                      max                       total
     *
     * capped offset ranges from minDistanceFromEdge (min) to max
     */

    const cappedOffset = cap(offsetInt, max, minDistanceFromEdge); // the tail width is subtracted from the cappedOffset because css will offset from the edge of the tail, not the center

    return `${cappedOffset - halfTailWidth + (this.isVertical ? 0 : halfTailHeight)}px`;
  }

  calculateOffsetSide() {
    if (!this.alignmentHasValidOrigin()) {
      return this.isVertical ? 'left' : 'top';
    }

    return offsetSideFromOriginAndTailSide[this.props.tailAlignment.origin][this.props.tailSide];
  }
  /* eslint-disable @typescript-eslint/prefer-optional-chain */


  alignmentHasValidOrigin() {
    return this.props.tailAlignment && this.props.tailAlignment.origin && offsetSideFromOriginAndTailSide[this.props.tailAlignment.origin];
  }

  dimensionsHaveChanged() {
    // if we don't have a ref to the outer DIV yet, just return false.
    if (!this.divRef) return false;
    return this.divRef.clientHeight !== this.state.contentHeight || this.divRef.clientWidth !== this.state.contentWidth;
  }

  render() {
    const {
      className,
      inline,
      onKeyDown,
      role,
      maxWidth,
      containerFluid,
      zIndex,
      tailSize,
      tailSide,
      attributes,
      children,
      tailRef,
      variant,
      textAlign,
      isDismissible,
      onDismiss
    } = this.props;
    const {
      color,
      backgroundColor,
      borderColor,
      shadow
    } = variants[variant] || variants.dark;
    return jsxs(SpeechBubbleContent, Object.assign({
      backgroundColor: backgroundColor,
      borderColor: borderColor,
      className: className,
      color: color,
      containerFluid: containerFluid,
      inline: inline,
      isDismissible: isDismissible,
      maxWidth: maxWidth,
      onKeyDown: onKeyDown,
      ref: this.setDivRef,
      role: role,
      shadow: shadow,
      tailHeight: PIXEL_WIDTH_FROM_TAIL_SIZE[tailSize] / 2,
      tailSide: tailSide,
      textAlign: textAlign,
      zIndex: zIndex
    }, attributes, {
      children: [children, isDismissible && jsx(DismissibleDiv, {
        children: jsx(DismissButton, Object.assign({
          "aria-label": "close",
          className: className,
          onClick: onDismiss
        }, {
          children: jsx(IconNavigationCloseCircle, {
            fill: color,
            height: 24
          }, void 0)
        }), void 0)
      }, void 0), jsx(PopoverTail, {
        borderColor: borderColor,
        fill: backgroundColor,
        offset: this.calculateOffset(),
        offsetSide: this.calculateOffsetSide(),
        ref: tailRef,
        side: tailSide,
        width: this.state.renderedTailWidth
      }, void 0)]
    }), void 0);
  }

}
PopoverInternal.displayName = 'PopoverInternal';
PopoverInternal.defaultProps = {
  maxWidth: 'none',
  tailSide: 'bottom',
  tailSize: 'md',
  textAlign: 'center',
  variant: 'dark'
};

export { PIXEL_WIDTH_FROM_TAIL_SIZE, PopoverInternal, offsetSideFromOriginAndTailSide, variants };
